import { useLazyQuery } from '@apollo/client';
import { UseFormSetValue } from 'react-hook-form';
import MagicWand from '~/assets/icons/MagicWand';
import { GENERATE_SUGGESTIONS } from '~/services/onboarding';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface CreateSuggestionsProps {
  filledText: string;
  setValue: UseFormSetValue<any>;
  fieldName: string;
  enablePulseHint?: boolean;
}

export const CreateSuggestions = ({
  filledText,
  fieldName,
  setValue,
  enablePulseHint = false,
}: CreateSuggestionsProps) => {
  const [query, { loading }] = useLazyQuery(GENERATE_SUGGESTIONS);
  const [displayPulseAnimation, setDisplayPulseAnimation] = useState(false);
  const { i18n } = useTranslation();

  useEffect(() => {
    if (enablePulseHint && filledText && !loading && !displayPulseAnimation) {
      setDisplayPulseAnimation(true);
    } else if (!filledText && displayPulseAnimation) {
      setDisplayPulseAnimation(false);
    }
  }, [filledText, enablePulseHint, loading, displayPulseAnimation]);

  const handleIconClick = async () => {
    if (displayPulseAnimation) {
      setDisplayPulseAnimation(false);
    }

    const { data } = await query({
      variables: {
        data: {
          question: fieldName,
          text: filledText,
          language: i18n.language,
        },
      },
    });

    setValue(fieldName, data.response.data);
  };

  return (
    <button
      data-testid="suggestions-button"
      onClick={handleIconClick}
      className={`btn btn-circle btn-xs ${
        displayPulseAnimation ? 'animate-shadow-pulse' : ''
      }`}
      disabled={!filledText || loading}
    >
      {loading ? (
        <div className="btn-circle btn-xs flex items-center rounded-full bg-cyan-100 text-cyan-500">
          <span className="loading loading-ball loading-xs"></span>
          <span className="loading loading-ball loading-xs"></span>
        </div>
      ) : (
        <MagicWand
          className={`duration-400 h-4 w-4 transition-all ${
            filledText
              ? 'cursor-pointer fill-info'
              : 'cursor-not-allowed fill-base-content'
          }`}
        />
      )}
    </button>
  );
};
